import Permission from '@constants/permission'
import React, { useState } from 'react'
import { connect } from 'react-redux'

import MaterialTable from '@material-table/core'
import { Switch } from '@mui/material'

import {
  deleteOverride,
  fetchHours,
  setOverrideClosed,
  setOverrideHoliday,
  updateHoursRecord,
} from '../../../services/store-hours-service'
import { fetchStore } from '../../../services/store-service'
import { setFlashMessage } from '@store/reducers/flash-message/actions'

import HolidayHourDialog from './holiday-hour-dialog'
import withRouter from '@utils/legacy/with-router'
import { checkPermsAtStore } from '@utils/has-permission'
import { getFormattedTime } from '../../../utils/date-utils'
import { selectCurrentPermissionNames } from '@store/selectors'

function getStoreId(props) {
  return parseInt(props.match.params.storeId, 10)
}

const mapStateToProps = (state, ownProps) => ({
  store: state.Stores.stores[getStoreId(ownProps)] || {},
  overrides: state.StoreHoursOverrides[getStoreId(ownProps)] || {},
  permissions: selectCurrentPermissionNames(state),
  storeIds: state.CurrentUser.user.store_ids,
})

const mapDispatchToProps = (dispatch, ownProps) => {
  const storeId = getStoreId(ownProps)

  return {
    actions: {
      deleteOverride: (date) => deleteOverride(dispatch)(storeId, date),
      fetchStore: () => dispatch(fetchStore(storeId)),
      fetchHours: () => fetchHours(dispatch)(storeId),
      updateHoursRecord: (data) => updateHoursRecord(dispatch)(storeId, data),
      setClosed: (date, closed) =>
        setOverrideClosed(dispatch)(storeId, date, closed),
      setHoliday: (date, holiday) =>
        setOverrideHoliday(dispatch)(storeId, date, holiday),
      setFlashMessage: (message, variant) =>
        dispatch(setFlashMessage(message, variant)),
    },
  }
}

function StoreHolidays(props) {
  const { actions } = props
  const [hasFetchedData, setHasFetchedData] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [dialogLoading, setDialogLoading] = useState(false)
  const [editStoreHour, setEditStoreHour] = useState(null)

  const canEdit = checkPermsAtStore(
    props.permissions,
    Permission.STORES_UPDATE_HOURS,
    props.storeIds,
    getStoreId(props),
  )

  if (!hasFetchedData) {
    setHasFetchedData(true)
    Promise.all([actions.fetchStore(), actions.fetchHours()]).catch(
      (err) => actions.setFlashMessage(err.message),
      'error',
    )
  }

  return (
    <div>
      <HolidayHourDialog
        loading={dialogLoading}
        onClose={() => {
          setEditDialogOpen(false)
          setEditStoreHour(null)
        }}
        onSubmit={(data) => {
          setDialogLoading(true)
          actions
            .updateHoursRecord(data)
            .then(() => {
              actions.setFlashMessage('Success', 'success')
              setEditDialogOpen(false)
              setDialogLoading(false)
            })
            .catch((err) => {
              actions.setFlashMessage(err.message, 'error')
              setDialogLoading(false)
            })
          setEditStoreHour(null)
        }}
        open={editDialogOpen}
        storeHour={editStoreHour}
        storeId={getStoreId(props)}
      />
      <MaterialTable
        actions={
          canEdit
            ? [
                {
                  icon: 'add',
                  isFreeAction: true,
                  tooltip: 'Add Holiday',
                  onClick: () => setEditDialogOpen(true),
                },
                {
                  tooltip: 'Edit Holiday Hour',
                  icon: 'edit',
                  onClick: (e, rowData) => {
                    setEditStoreHour(rowData)
                    setEditDialogOpen(true)
                  },
                },
              ]
            : []
        }
        columns={[
          {
            title: 'Date',
            field: 'override_date',
            defaultSort: 'asc',
            render: (rowData) => rowData.override_date,
          },
          {
            title: 'Opens',
            field: 'opening_time',
            render: (rowData) => getFormattedTime(rowData.opening_time),
          },
          {
            title: 'Closes',
            field: 'closing_time',
            render: (rowData) => getFormattedTime(rowData.closing_time),
          },
          {
            title: 'Closed',
            editable: 'never',
            field: 'is_closed',
            render: (rowData) => {
              return (
                <Switch
                  checked={rowData.is_closed}
                  disabled={!canEdit}
                  onChange={(event) => {
                    actions.setClosed(
                      rowData.override_date,
                      event.target.checked,
                    )
                  }}
                />
              )
            },
          },
          {
            title: 'Holiday',
            editable: 'never',
            field: 'is_holiday',
            render: (rowData) => {
              return (
                <Switch
                  checked={rowData.is_holiday}
                  disabled={!canEdit}
                  onChange={(event) => {
                    actions.setHoliday(
                      rowData.override_date,
                      event.target.checked,
                    )
                  }}
                />
              )
            },
          },
        ]}
        data={Object.values(props.overrides)
          .filter((row) => row.archived_at == null)
          .map((x) => {
            x.override_date = x.override_date.split('T')[0]
            return x
          })}
        editable={
          canEdit
            ? {
                onRowDelete: (rowData) => {
                  return actions.deleteOverride(rowData.override_date)
                },
              }
            : undefined
        }
        options={{
          actionsColumnIndex: 5,
        }}
        title={`Upcoming Holidays - ${props.store.name}`}
      />
    </div>
  )
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(StoreHolidays),
)
